import React from 'react';
import { components as SelectComponents } from 'react-select';
import { useTranslation } from 'react-i18next';

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path
      d="M1.5 5l2.5 2.5 4.5-4.5"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckboxOption = ({ isSelected, isFocused, children, innerProps, innerRef }: any) => (
  <div
    ref={innerRef}
    {...innerProps}
    className={['wf-option', isFocused && 'wf-option--focused'].filter(Boolean).join(' ')}
  >
    <span className={['wf-checkbox', isSelected && 'wf-checkbox--checked'].filter(Boolean).join(' ')} aria-hidden="true">
      {isSelected && <CheckIcon />}
    </span>
    <span className="wf-option__label">{children}</span>
  </div>
);

const SingleCheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8l3.5 3.5 6.5-7"
      stroke="#2463eb"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SingleOption = ({ isSelected, isFocused, children, innerProps, innerRef }: any) => (
  <div
    ref={innerRef}
    {...innerProps}
    className={[
      'wf-single-option',
      isFocused && 'wf-single-option--focused',
      isSelected && 'wf-single-option--selected',
    ].filter(Boolean).join(' ')}
  >
    <span className="wf-single-option__label">{children}</span>
    {isSelected && (
      <span style={{ flexShrink: 0, lineHeight: '20px', display: 'flex', alignItems: 'center' }}>
        <SingleCheckIcon />
      </span>
    )}
  </div>
);

export const singleSelectComponents = { Option: SingleOption };

/** Hook that returns react-select component overrides for multi-select filters. */
export const useMultiSelectComponents = () => {
  const { t } = useTranslation();

  return React.useMemo(() => ({
    ValueContainer: ({ children, getValue, ...props }: any) => {
      const values = getValue();
      const childArray = React.Children.toArray(children);
      const input = childArray[childArray.length - 1];

      if (values.length === 0) {
        return (
          <SelectComponents.ValueContainer {...props} getValue={getValue}>
            {children}
          </SelectComponents.ValueContainer>
        );
      }

      const label = values[0].label;
      const truncated = label.length > 15 ? label.slice(0, 15) + '…' : label;
      const text =
        values.length === 1
          ? truncated
          : `${truncated}, +${values.length - 1}`;

      return (
        <SelectComponents.ValueContainer {...props} getValue={getValue}>
          <span className="wf-select__single-value">{text}</span>
          {input}
        </SelectComponents.ValueContainer>
      );
    },

    Option: CheckboxOption,
    ClearIndicator: () => null,

    Menu: ({ children, selectProps, ...props }: any) => {
      const handleClear = () => {
        selectProps.onChange([], {
          action: 'clear',
          removedValues: selectProps.value || [],
        });
      };

      const currentValues: string[] = ((selectProps.value || []) as any[]).map(
        (o) => o.stringValue ?? String(o.value)
      );
      const defaultValues: string[] = selectProps.defaultOptionValues ?? [];
      const isAtDefault =
        currentValues.length === defaultValues.length &&
        [...currentValues].sort().join('\0') === [...defaultValues].sort().join('\0');

      return (
        <SelectComponents.Menu {...props} selectProps={selectProps}>
          {children}
          <div className="wf-clear-footer">
            <button
              type="button"
              className="wf-clear-btn"
              onClick={handleClear}
              disabled={isAtDefault}
            >
              {t('Clear')}
            </button>
          </div>
        </SelectComponents.Menu>
      );
    },
  }), [t]);
};
